import { WarpField } from "./warp-field";
import { pointerDrawRegions } from "./draw-regions";
import { createPointerField } from "./pointer-field";
import { RIBBON_PALETTES } from "./palettes";
import { RibbonPerfMonitor } from "./perf-monitor";
import { DEFAULT_RIBBON_FRAMING_HEIGHT } from "./presets/shared";
import { ProgramCache, coverageMarginForState, shaderConstantsForPreset, shaderFeaturesForPreset, shaderFeaturesForState, shaderVariantKey, } from "./program-cache";
import { clamp, easeOutCubic, mixf, mobileFramingMix, resolveCurveProfile, resolveStates, smooth, } from "./state";
import { activeStepIndex, expandWaypointSteps, measureTimeline, stateAtScroll, } from "./timeline";
import { resolveWaypointTargets } from "./waypoints";
const IDLE_SPEED_MULTIPLIER = 0.5;
const MAX_FRAME_DELTA_SECONDS = 0.1;
const DESKTOP_SCENE_UNIT_PX = 900;
const MOBILE_SCENE_UNIT_PX = 844;
const DESKTOP_REFERENCE_ASPECT = 1440 / 900;
const MOBILE_REFERENCE_ASPECT = 390 / 844;
/** Editor mode: how much idle "time" one CSS px of scroll is worth. */
const EDITOR_SECONDS_PER_SCROLL_PX = 0.0025;
/**
 * Adaptive quality. The signal is GPU DUTY — milliseconds of GPU work per
 * second of wall time — because that, not per-frame time, is what heats a
 * laptop and drains its battery. Above the budget the controller steps down a
 * tier; well below it (with hysteresis and a long dwell) it steps back up.
 *   tier 0  full rate, 1× resolution (DPR ≤ 1)
 *   tier 1  idle drift redrawn at 30 fps          — same motion path, half the duty
 *   tier 2  same as tier 1 at the current 1× cap (retained for controller compatibility)
 *   tier 3  tier 2 + idle drift at 20 fps
 * Scroll, pointer and reveal frames always redraw at the current resolution.
 * `auroraQuality=full` (or the older `auroraCadence=full`) pins tier 0.
 */
const QUALITY_DUTY_BUDGET = 0.45;
const QUALITY_DUTY_RECOVER = 0.15;
/** Displayed-frame interval used as the signal when timer queries are unavailable. */
const QUALITY_FRAME_INTERVAL_MS = 20;
/** Dwell (ms) before stepping down / up. */
const QUALITY_STEP_DOWN_DWELL_MS = 1500;
const QUALITY_STEP_UP_DWELL_MS = 6000;
const QUALITY_MAX_TIER = 3;
const QUALITY_IDLE_INTERVAL_S = [1 / 60, 1 / 30, 1 / 30, 1 / 20];
const QUALITY_DPR_CAP = [1, 1, 1, 1];
/**
 * The ribbon renderer, ported from the prototype's MorphingBackground and
 * separated from React. One engine owns one canvas and one WebGL2 context for
 * its whole life; `setScene` swaps composition/waypoints without rebuilding either.
 *
 * The host owns the clock (`frame`), visibility (`setVisible`) and layout
 * changes (`measure`). The engine owns everything between scroll position and
 * pixels. Without WebGL2 the timeline still runs and `onState` still fires, so
 * the scroll choreography is observable (and testable) without a GPU.
 */
export class RibbonEngine {
    hasContext;
    canvas;
    root;
    scroll;
    mode;
    onState;
    diagnostics;
    gl = null;
    warpField = null;
    warpEnabled = false;
    warpLinear = false;
    warpBundle = null;
    programs = null;
    perf = null;
    buffer = null;
    pointerField = null;
    interactiveBundle = null;
    idleBundle = null;
    configuredProgramVersions = new WeakMap();
    configuredStateVersions = new WeakMap();
    uniformVersion = 1;
    stateVersion = 0;
    compilationGeneration = 0;
    activePosition = -1;
    optimized;
    freezeTime;
    coverageEnabled;
    paletteRecurrence;
    adaptiveCadence;
    qualityPinned;
    /** `auroraPointer=0` disables the pointer lens (deterministic captures). */
    pointerAllowed;
    scene = null;
    preset = null;
    palette = null;
    disposed = false;
    visible = true;
    contextLost = false;
    lastRenderedState = null;
    lastPointer = { x: 0, y: 0, velocityX: 0, velocityY: 0, strength: 0 };
    lastClear = null;
    wasOffscreen = false;
    currentState = null;
    stateDirty = true;
    time = 0;
    previousFrameTimestamp = null;
    lastScrollY = 0;
    lastSubmittedTime = -Infinity;
    qualityTier = 0;
    qualityDwellStart = null;
    qualityDwellDirection = 0;
    cssWidth = 1;
    cssHeight = 1;
    crossAxisScale = 1;
    sceneHeightRatio = 1;
    framingAspect = 1;
    pointerRadiusPx = 1;
    curveProfile = resolveCurveProfile(undefined, 0);
    measured = [];
    curveProgressSegments = [];
    initialState = null;
    releaseY = Infinity;
    releaseTopY = Infinity;
    pinned = true;
    timedReveal = null;
    timedRevealProgress = 0;
    timedRevealTarget = 0;
    timedRevealRetracting = false;
    timedRevealRetractionEdgeReveal = 1;
    timedRevealPreviousScrollY = 0;
    timedRevealInitialized = false;
    lastReported = {
        ready: false,
        stepIndex: -1,
        stateId: null,
        position: "offscreen",
    };
    constructor(options) {
        this.canvas = options.canvas;
        this.root = options.root;
        this.scroll = options.scroll;
        this.mode = options.mode;
        this.onState = options.onState;
        this.diagnostics = options.diagnostics ?? null;
        const d = this.diagnostics;
        this.optimized = d?.get("auroraMode") !== "baseline";
        this.freezeTime = d?.get("auroraFreeze") === "1";
        this.coverageEnabled = this.optimized && d?.get("auroraCoverage") !== "0";
        this.paletteRecurrence = this.optimized && d?.get("auroraPalette") !== "baseline";
        const pinnedTier = /^tier([0-3])$/.exec(d?.get("auroraQuality") ?? "");
        this.qualityPinned = pinnedTier !== null;
        if (pinnedTier)
            this.qualityTier = Number(pinnedTier[1]);
        this.adaptiveCadence = this.qualityPinned ||
            (d?.get("auroraQuality") !== "full" && d?.get("auroraCadence") !== "full");
        this.pointerAllowed = d?.get("auroraPointer") !== "0";
        this.lastScrollY = this.scroll.y();
        this.hasContext = this.acquireContext();
        this.canvas.addEventListener("webglcontextlost", this.onContextLost);
        this.canvas.addEventListener("webglcontextrestored", this.onContextRestored);
    }
    // ── Lifecycle ─────────────────────────────────────────
    acquireContext() {
        const gl = this.canvas.getContext("webgl2", {
            alpha: false,
            antialias: false,
            depth: false,
            stencil: false,
            preserveDrawingBuffer: false,
            powerPreference: this.mode === "editor" ? "low-power" : "high-performance",
        });
        if (!gl)
            return false;
        const buffer = gl.createBuffer();
        if (!buffer)
            return false;
        gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
        this.gl = gl;
        this.warpEnabled = this.diagnostics?.get("auroraWarp") === "half" && Boolean(gl.getExtension("EXT_color_buffer_float"));
        this.warpLinear = this.warpEnabled && Boolean(gl.getExtension("OES_texture_float_linear"));
        this.buffer = buffer;
        // A fresh `alpha: false` drawing buffer is opaque black: paint paper before
        // anything else can composite it.
        this.clearToPaper();
        this.programs = new ProgramCache(gl);
        this.perf = new RibbonPerfMonitor(gl, this.optimized ? "optimized" : "baseline", this.diagnostics);
        this.perf.setQuality(this.qualityPinned ? `tier ${this.qualityTier} (pinned)` : "full");
        this.perf.setCompilationMode(this.programs.parallelCompile ? "parallel" : "synchronous");
        if (this.perf.enabled && typeof window !== "undefined") {
            window.__ribbonPerf = {
                snapshot: () => this.perf?.snapshot(),
                reset: () => this.perf?.reset(),
                capture: (options) => this.capture(options),
                timeline: () => ({
                    scrollY: this.scroll.y(),
                    viewport: [this.cssWidth, this.cssHeight],
                    releaseY: this.releaseY,
                    releaseTopY: this.releaseTopY,
                    waypoints: this.scene?.waypoints ?? [],
                    steps: this.measured.map((step) => ({
                        stateId: step.stateId,
                        blockId: step.blockId,
                        start: Math.round(step.start),
                        end: Math.round(step.end),
                    })),
                }),
            };
        }
        this.activePosition = -1;
        this.configuredProgramVersions;
        return true;
    }
    releaseContext() {
        this.warpField?.destroy();
        this.warpField = null;
        this.warpBundle = null;
        this.programs?.destroy();
        this.programs = null;
        this.perf?.destroy();
        this.perf = null;
        if (this.gl && this.buffer)
            this.gl.deleteBuffer(this.buffer);
        this.buffer = null;
        this.interactiveBundle = null;
        this.idleBundle = null;
        this.gl = null;
    }
    onContextLost = (event) => {
        event.preventDefault();
        this.contextLost = true;
        this.perf?.setRenderState("paused: context lost");
    };
    onContextRestored = () => {
        this.releaseContext();
        this.contextLost = false;
        this.resetPacing();
        this.lastClear = null;
        if (this.acquireContext() && this.preset) {
            this.compileForPreset(this.preset);
            this.uniformVersion += 1;
            this.stateDirty = true;
            this.measure();
            this.frame(performance.now());
        }
    };
    destroy() {
        this.disposed = true;
        this.canvas.removeEventListener("webglcontextlost", this.onContextLost);
        this.canvas.removeEventListener("webglcontextrestored", this.onContextRestored);
        this.pointerField?.destroy();
        this.pointerField = null;
        this.releaseContext();
    }
    // ── Scene ─────────────────────────────────────────────
    setScene(scene) {
        if (this.disposed)
            return;
        this.resetPacing();
        this.lastRenderedState = null;
        this.lastClear = null;
        this.scene = scene;
        const preset = scene?.composition ?? null;
        this.canvas.style.opacity = scene ? String(scene.opacity) : "";
        if (!preset) {
            this.preset = null;
            this.palette = null;
            this.measured = [];
            this.initialState = null;
            this.currentState = null;
            this.pointerField?.destroy();
            this.pointerField = null;
            this.clearToPaper();
            this.report({ ready: true, stepIndex: -1, stateId: null, position: "offscreen" });
            return;
        }
        const presetChanged = this.preset !== preset;
        this.preset = preset;
        this.palette = RIBBON_PALETTES[preset.palette];
        if (presetChanged) {
            this.uniformVersion += 1;
            this.compileForPreset(preset);
            if (this.pointerField)
                this.pointerField.setConfig(preset.interaction);
            else if (this.gl) {
                this.pointerField = createPointerField(this.canvas, preset.interaction, {
                    enabled: this.mode === "live" && this.pointerAllowed,
                });
            }
            // A different composition starts its own clock, like a route change.
            this.time = 0;
            this.previousFrameTimestamp = null;
        }
        this.currentState = null;
        this.stateDirty = true;
        this.releaseY = Infinity;
        this.releaseTopY = Infinity;
        this.pinned = true;
        this.scroll.positionCanvas(this.canvas, "pinned", this.scroll.y());
        this.timedRevealInitialized = false;
        this.timedRevealProgress = 0;
        this.timedRevealTarget = 0;
        this.timedRevealRetracting = false;
        this.measure();
        this.frame(performance.now());
    }
    /**
     * Bring up the programs a composition needs WITHOUT blocking the thread the
     * author is clicking on. Each bundle is requested through the parallel
     * compiler and lands in a callback; until then `bundleForState` finds nothing
     * and frames are skipped, so the previous shape simply stays on screen for
     * the second or so the driver takes. Editor and reduced modes never use the
     * pointer, so they only ever compile the idle program.
     */
    compileForPreset(preset) {
        const programs = this.programs;
        if (!programs)
            return;
        const generation = ++this.compilationGeneration;
        const constants = shaderConstantsForPreset(preset);
        this.interactiveBundle = null;
        this.idleBundle = null;
        this.warpBundle = null;
        const idleFeatures = this.withPaletteMode(shaderFeaturesForPreset(preset, false, this.optimized));
        const wantsPointer = this.mode === "live" && this.pointerAllowed;
        const land = (slot) => (bundle) => {
            if (this.disposed || this.preset !== preset || generation !== this.compilationGeneration)
                return;
            if (slot === "idle")
                this.idleBundle = bundle;
            else
                this.interactiveBundle = bundle;
            this.stateDirty = true;
            this.perf?.setRenderState("rendering");
            this.frame(performance.now());
        };
        this.perf?.setRenderState("compiling");
        this.requestBundle(idleFeatures, constants, land("idle"));
        if (this.warpEnabled) {
            this.requestBundle({ ...idleFeatures, interaction: false, warp: "prepass" }, constants, (bundle) => {
                if (this.disposed || this.preset !== preset || generation !== this.compilationGeneration)
                    return;
                if (bundle?.variant.startsWith("prepass:")) {
                    this.warpBundle = bundle;
                    this.stateDirty = true;
                    this.frame(performance.now());
                }
                else {
                    this.warpEnabled = false;
                    this.warpField?.destroy();
                    this.warpField = null;
                    this.compileForPreset(preset);
                }
            });
        }
        if (wantsPointer) {
            this.requestBundle(this.withPaletteMode(shaderFeaturesForPreset(preset, true, this.optimized)), constants, land("interactive"));
        }
        this.prewarmStateBundles(preset, wantsPointer);
    }
    /** Parallel compile when the driver offers it; otherwise compile on the next tick. */
    requestBundle(features, constants, onReady) {
        const programs = this.programs;
        if (!programs)
            return;
        const cached = programs.get(features, constants);
        if (cached) {
            onReady(cached);
            return;
        }
        if (programs.prewarm(features, constants, onReady))
            return;
        window.setTimeout(() => {
            if (this.disposed || !this.programs)
                return;
            try {
                onReady(this.programs.compile(features, constants));
            }
            catch (error) {
                console.warn("Ribbon WebGL scene failed to initialize", error);
                onReady(null);
            }
        }, 0);
    }
    withPaletteMode(features) {
        return {
            ...features,
            paletteRecurrence: this.paletteRecurrence,
            sharedFirstOctave: this.optimized && this.diagnostics?.get("auroraFirstOctave") !== "paired",
            ...(this.warpEnabled ? { warp: "sample", warpLinear: this.warpLinear } : {}),
        };
    }
    prewarmStateBundles(preset, withPointer) {
        const programs = this.programs;
        if (!programs)
            return;
        const constants = shaderConstantsForPreset(preset);
        const combinations = new Map();
        for (const state of preset.states) {
            for (const interaction of withPointer ? [false, true] : [false]) {
                const features = this.withPaletteMode(this.optimized
                    ? {
                        interaction,
                        shape: preset.shape,
                        curve: Boolean(preset.curveProfile) && Math.abs(state.curveStrength) > 0.000001,
                        funnel: Math.abs(state.funnelStrength ?? 0) > 0.000001,
                        optimized: true,
                    }
                    : shaderFeaturesForPreset(preset, interaction, false));
                combinations.set(shaderVariantKey(features), features);
            }
        }
        combinations.forEach((features) => programs.prewarm(features, constants));
    }
    bundleForState(state, interaction) {
        const preset = this.preset;
        const programs = this.programs;
        if (!preset || !programs)
            return null;
        const features = this.withPaletteMode(shaderFeaturesForState(preset, state, interaction, this.optimized));
        return (programs.get(features, shaderConstantsForPreset(preset)) ??
            (interaction ? this.interactiveBundle : (this.idleBundle ?? this.interactiveBundle)));
    }
    // ── Layout ────────────────────────────────────────────
    /** Re-read viewport size, resize the framebuffer, and re-measure waypoints. */
    measure() {
        if (this.disposed)
            return;
        const preset = this.preset;
        this.uniformVersion += 1;
        const viewport = this.scroll.viewport();
        // Explicit benchmark override; production remains capped at one pixel per
        // CSS pixel. Hold auroraQuality=full when isolating resolution cost.
        const diagnosticDpr = Number(this.diagnostics?.get("auroraDpr"));
        const dprCap = diagnosticDpr >= 0.5 && diagnosticDpr <= 1.5
            ? diagnosticDpr
            : QUALITY_DPR_CAP[this.qualityTier];
        const pixelRatio = this.mode === "editor"
            ? 1
            : Math.min(window.devicePixelRatio || 1, dprCap);
        this.cssWidth = Math.max(1, viewport.width);
        this.cssHeight = Math.max(1, viewport.height);
        const width = Math.max(1, Math.floor(this.cssWidth * pixelRatio));
        const height = Math.max(1, Math.floor(this.cssHeight * pixelRatio));
        if (this.canvas.width !== width || this.canvas.height !== height) {
            this.lastClear = null;
            this.canvas.width = width;
            this.canvas.height = height;
            // Resizing resets the drawing buffer to opaque black; paint paper at once
            // so nothing black can flash before the next frame lands.
            this.gl?.viewport(0, 0, width, height);
            this.clearToPaper();
        }
        this.gl?.viewport(0, 0, width, height);
        if (this.warpEnabled && this.gl &&
            (!this.warpField || this.warpField.width !== Math.ceil(width / 2) + 2 || this.warpField.height !== Math.ceil(height / 2) + 2)) {
            this.warpField?.destroy();
            this.warpField = WarpField.create(this.gl, width, height);
            if (!this.warpField) {
                this.warpEnabled = false;
                if (preset)
                    this.compileForPreset(preset);
            }
        }
        if (!preset)
            return;
        const framingMix = mobileFramingMix(this.cssWidth, preset.mobileBreakpoint);
        const sceneUnit = mixf(DESKTOP_SCENE_UNIT_PX, MOBILE_SCENE_UNIT_PX, framingMix);
        this.crossAxisScale = this.cssHeight / Math.max(sceneUnit, 1);
        const authoredFrame = preset.framingHeight ?? DEFAULT_RIBBON_FRAMING_HEIGHT;
        const framingHeight = mixf(authoredFrame.desktop, authoredFrame.mobile, framingMix);
        this.sceneHeightRatio = this.cssHeight / Math.max(framingHeight, 1);
        this.framingAspect = mixf(DESKTOP_REFERENCE_ASPECT, MOBILE_REFERENCE_ASPECT, framingMix);
        const layout = preset.interaction.viewport;
        this.pointerRadiusPx = clamp(Math.min(this.cssWidth, this.cssHeight) * layout.radiusViewportRatio, layout.minRadiusPx, layout.maxRadiusPx);
        this.pointerField?.invalidateBounds();
        this.updateTimeline();
    }
    updateTimeline() {
        const preset = this.preset;
        const scene = this.scene;
        if (!preset || !scene)
            return;
        const mobileMix = mobileFramingMix(this.cssWidth, preset.mobileBreakpoint);
        const tablet = preset.tabletBreakpoint !== undefined && this.cssWidth <= preset.tabletBreakpoint;
        this.curveProfile = resolveCurveProfile(preset.curveProfile, mobileMix);
        const states = resolveStates(preset.states, mobileMix, tablet);
        this.initialState = states.get(preset.initialStateId) ?? null;
        const targets = resolveWaypointTargets(this.root, scene.waypoints, (element) => this.scroll.documentTop(element));
        const steps = expandWaypointSteps(preset, targets);
        const timeline = measureTimeline(steps, states, this.cssHeight, this.initialState);
        this.measured = timeline.measured;
        this.releaseY = timeline.releaseY;
        this.releaseTopY = timeline.releaseTopY;
        this.timedReveal = timeline.timedReveal;
        this.curveProgressSegments = timeline.curveProgressSegments;
        const scrollY = this.scroll.y();
        if (!this.timedRevealInitialized && this.timedReveal) {
            this.timedRevealTarget = scrollY >= this.timedReveal.startY ? 1 : 0;
            this.timedRevealPreviousScrollY = scrollY;
            this.timedRevealInitialized = true;
        }
        this.currentState = null;
        this.stateDirty = true;
        this.updatePin(scrollY);
    }
    updatePin(scrollY) {
        const shouldPin = scrollY < this.releaseY;
        if (this.mode === "editor") {
            // The editor canvas is always positioned explicitly inside its frame.
            this.pinned = shouldPin;
            this.scroll.positionCanvas(this.canvas, shouldPin ? "pinned" : { releasedTop: this.releaseTopY }, scrollY);
            return;
        }
        if (shouldPin === this.pinned)
            return;
        this.pinned = shouldPin;
        this.scroll.positionCanvas(this.canvas, shouldPin ? "pinned" : { releasedTop: this.releaseTopY }, scrollY);
    }
    setVisible(visible) {
        if (this.visible === visible)
            return;
        this.visible = visible;
        this.resetPacing();
        this.stateDirty = true;
        this.pointerField?.setSceneVisible(visible);
        this.perf?.resetFrameClock();
        this.perf?.setRenderState(visible ? "rendering" : "paused: hidden");
    }
    resetPacing() {
        this.previousFrameTimestamp = null;
        this.frameIntervalEstimate = 16.7;
        this.lastSubmittedTime = -Infinity;
        this.qualityDwellStart = null;
        this.qualityDwellDirection = 0;
        this.perf?.reset();
    }
    // ── Frames ────────────────────────────────────────────
    report(next) {
        const last = this.lastReported;
        if (last.ready === next.ready &&
            last.stepIndex === next.stepIndex &&
            last.stateId === next.stateId &&
            last.position === next.position)
            return;
        this.lastReported = next;
        this.onState?.(next);
    }
    positionAt(scrollY) {
        if (this.scene?.opacity === 0)
            return "offscreen";
        if (this.pinned)
            return "pinned";
        if (this.mode === "editor")
            return "released";
        const outsideViewport = this.releaseTopY + this.cssHeight <= scrollY ||
            this.releaseTopY >= scrollY + this.cssHeight;
        return outsideViewport ? "offscreen" : "released";
    }
    clearToPaper() {
        const gl = this.gl;
        if (!gl || this.contextLost)
            return;
        gl.clearColor(0.992, 0.992, 1, 1);
        gl.clear(gl.COLOR_BUFFER_BIT);
    }
    /** Advance the scene to `nowMs` and draw if anything changed (or time moved). */
    frame(nowMs) {
        if (this.disposed || !this.visible || !this.preset || !this.initialState)
            return;
        // Measure opportunities to render, including deliberately skipped draws.
        this.perf?.recordTick(nowMs);
        if (this.previousFrameTimestamp !== null && nowMs > this.previousFrameTimestamp) {
            this.frameIntervalEstimate = this.frameIntervalEstimate * 0.9 + (nowMs - this.previousFrameTimestamp) * 0.1;
        }
        const scrollY = this.scroll.y();
        const scrollDelta = scrollY - this.lastScrollY;
        if (scrollDelta !== 0) {
            this.stateDirty = true;
            this.lastScrollY = scrollY;
            this.updatePin(scrollY);
            if (!this.pinned || this.mode === "editor")
                this.pointerField?.invalidateBounds();
        }
        else if (this.mode === "editor") {
            // Keep the released/pinned position honest after a measure() moved the frame.
            this.updatePin(scrollY);
        }
        const stepIndex = activeStepIndex(this.measured, scrollY);
        const stateId = stepIndex >= 0 ? this.measured[stepIndex].stateId : this.preset.initialStateId;
        this.report({ ready: true, stepIndex, stateId, position: this.positionAt(scrollY) });
        this.drawFrame(nowMs, scrollY, scrollDelta);
    }
    drawFrame(timestamp, scrollY, scrollDelta) {
        const gl = this.gl;
        const perf = this.perf;
        let stateChanged = false;
        if (this.stateDirty || !this.currentState) {
            this.currentState = stateAtScroll(this.measured, this.initialState, this.curveProgressSegments, scrollY);
            this.stateDirty = false;
            stateChanged = true;
        }
        if (!this.currentState)
            return;
        let state = this.currentState;
        // ── Clock ──
        let frameSeconds = 1 / 60;
        if (this.mode === "editor") {
            frameSeconds = Math.abs(scrollDelta) * EDITOR_SECONDS_PER_SCROLL_PX;
            this.time += frameSeconds;
        }
        else if (this.mode === "reduced" || this.freezeTime) {
            frameSeconds = 0;
        }
        else if (this.previousFrameTimestamp !== null) {
            frameSeconds = clamp((timestamp - this.previousFrameTimestamp) / 1000, 0, MAX_FRAME_DELTA_SECONDS);
            this.time += frameSeconds;
        }
        this.previousFrameTimestamp = timestamp;
        // ── Timed reveal (landing endcap) ──
        const reveal = this.timedReveal;
        const previousRevealProgress = this.timedRevealProgress;
        if (reveal) {
            const previous = this.timedRevealPreviousScrollY;
            if (scrollY - previous < -0.01 &&
                this.timedRevealProgress > 0 &&
                scrollY <= reveal.reverseStartY) {
                if (!this.timedRevealRetracting) {
                    this.timedRevealRetractionEdgeReveal = this.timedRevealProgress;
                }
                this.timedRevealTarget = 0;
                this.timedRevealRetracting = true;
            }
            else if (scrollY >= reveal.startY &&
                (!this.timedRevealRetracting || !reveal.reverseScrollBound || scrollY >= reveal.reverseStartY)) {
                this.timedRevealTarget = 1;
                this.timedRevealRetracting = false;
            }
            this.timedRevealPreviousScrollY = scrollY;
            if (this.timedRevealRetracting && reveal.reverseScrollBound) {
                this.timedRevealProgress = clamp((scrollY - reveal.startY) / Math.max(reveal.reverseStartY - reveal.startY, 1), 0, 1);
                if (scrollY < reveal.startY) {
                    this.timedRevealTarget = 0;
                    this.timedRevealRetracting = false;
                }
            }
            else {
                const activeDurationMs = this.timedRevealTarget < this.timedRevealProgress ? reveal.reverseDurationMs : reveal.durationMs;
                // In editor mode the reveal advances with scroll distance rather than
                // wall time; in reduced mode it snaps so the ribbon is never stuck hidden.
                const dt = this.mode === "reduced" ? activeDurationMs / 1000 : frameSeconds;
                const progressDelta = dt / (activeDurationMs / 1000);
                if (this.timedRevealProgress < this.timedRevealTarget) {
                    this.timedRevealProgress = Math.min(this.timedRevealProgress + progressDelta, this.timedRevealTarget);
                }
                else if (this.timedRevealProgress > this.timedRevealTarget) {
                    this.timedRevealProgress = Math.max(this.timedRevealProgress - progressDelta, this.timedRevealTarget);
                }
            }
            if (this.timedRevealProgress > 0 || this.timedRevealTarget > 0 || scrollY >= reveal.startY) {
                state = {
                    ...reveal.state,
                    effectOpacity: this.timedRevealRetracting
                        ? smooth(this.timedRevealProgress)
                        : easeOutCubic(this.timedRevealProgress),
                    edgeReveal: this.timedRevealRetracting
                        ? this.timedRevealRetractionEdgeReveal
                        : this.timedRevealProgress,
                };
                stateChanged ||= previousRevealProgress !== this.timedRevealProgress ||
                    this.lastRenderedState?.effectOpacity !== state.effectOpacity ||
                    this.lastRenderedState?.edgeReveal !== state.edgeReveal;
            }
        }
        if (stateChanged)
            this.stateVersion += 1;
        if (!gl || this.contextLost || !this.programs)
            return;
        const pointer = this.pointerField?.update(frameSeconds) ?? {
            x: 0, y: 0, velocityX: 0, velocityY: 0, strength: 0,
        };
        this.lastRenderedState = state;
        this.lastPointer = pointer;
        const offscreen = this.positionAt(scrollY) === "offscreen";
        if (offscreen) {
            if (!this.wasOffscreen)
                this.resetPacing();
            this.wasOffscreen = true;
            perf?.setRenderState("paused: offscreen");
            return;
        }
        if (this.wasOffscreen) {
            this.wasOffscreen = false;
            this.resetPacing();
            stateChanged = true;
            this.stateVersion += 1;
            perf?.setRenderState("rendering");
        }
        const pointerActive = pointer.strength > 0.0001;
        const revealInFlight = Boolean(reveal) && this.timedRevealProgress !== this.timedRevealTarget;
        // ── Cadence ──
        // Nothing moved and nothing is animating: nothing to submit. (Only reachable
        // while time is frozen or in editor mode; live idle drift always moves.)
        if (!stateChanged && !pointerActive && !revealInFlight && frameSeconds === 0) {
            return;
        }
        // Adaptive quality: purely idle drift on a machine that is over budget is
        // redrawn less often (and, if that is still not enough, at DPR 1). Scroll,
        // pointer and reveal frames always submit. Same motion path throughout.
        if (this.adaptiveCadence && this.mode === "live") {
            if (state.effectOpacity > 0)
                this.updateQuality(perf, timestamp);
            const idleInterval = QUALITY_IDLE_INTERVAL_S[this.qualityTier];
            if (!stateChanged && !pointerActive && !revealInFlight &&
                this.time - this.lastSubmittedTime < idleInterval - 0.0005) {
                perf?.setCadence(this.qualityTier > 0 ? `idle 1/${Math.round(1 / idleInterval)}` : "full");
                return;
            }
        }
        perf?.setCadence(this.qualityTier > 0 ? `idle 1/${Math.round(1 / QUALITY_IDLE_INTERVAL_S[this.qualityTier])}` : "full");
        const cpuStart = perf ? performance.now() : 0;
        if (this.optimized && state.effectOpacity <= 0) {
            // A clear is not a material frame: no GPU sample, so the quality
            // controller is not fooled into stepping up by a hidden ribbon.
            const clearKey = state.background.join(",");
            if (this.lastClear === clearKey) {
                perf?.resetFrameClock();
                return;
            }
            this.lastClear = clearKey;
            perf?.setRenderState("paused: transparent");
            gl.clearColor(state.background[0], state.background[1], state.background[2], 1);
            gl.clear(gl.COLOR_BUFFER_BIT);
            this.lastSubmittedTime = this.time;
            if (perf?.enabled) {
                perf.setFrameInfo("clear:hidden", [this.cssWidth, this.cssHeight], [this.canvas.width, this.canvas.height], "clear");
                perf.recordFrame(timestamp, performance.now() - cpuStart);
            }
            // The next visible frame follows an intentional pause, not a missed deadline.
            perf?.resetFrameClock();
            return;
        }
        this.lastClear = null;
        const bundle = this.bundleForState(state, pointerActive);
        if (!bundle)
            return;
        this.submit(gl, bundle, state, pointer, timestamp, cpuStart);
    }
    /**
     * Step the quality tier from measured GPU time (timer queries) or, without
     * them, from displayed-frame pacing. Dwell times keep it from oscillating:
     * a step down needs 1.5 s over budget, a step up 6 s comfortably under it.
     */
    updateQuality(perf, timestamp) {
        if (this.qualityPinned)
            return;
        const duty = perf?.gpuDuty() ?? null;
        let direction = 0;
        if (duty !== null) {
            if (duty > QUALITY_DUTY_BUDGET)
                direction = 1;
            else if (duty < QUALITY_DUTY_RECOVER)
                direction = -1;
        }
        else if (perf && !perf.hasGpuTiming && this.previousFrameTimestamp !== null) {
            if (this.frameIntervalEstimate > QUALITY_FRAME_INTERVAL_MS)
                direction = 1;
            else if (this.frameIntervalEstimate < 17.5)
                direction = -1;
        }
        if (direction === 0 || direction !== this.qualityDwellDirection) {
            this.qualityDwellDirection = direction;
            this.qualityDwellStart = direction === 0 ? null : timestamp;
            return;
        }
        const dwell = timestamp - (this.qualityDwellStart ?? timestamp);
        const needed = direction === 1 ? QUALITY_STEP_DOWN_DWELL_MS : QUALITY_STEP_UP_DWELL_MS;
        if (dwell < needed)
            return;
        const next = clamp(this.qualityTier + direction, 0, QUALITY_MAX_TIER);
        this.qualityDwellStart = timestamp;
        this.qualityDwellDirection = 0;
        if (next === this.qualityTier)
            return;
        const dprChanged = QUALITY_DPR_CAP[next] !== QUALITY_DPR_CAP[this.qualityTier];
        this.qualityTier = next;
        perf?.setQuality(`tier ${next}`);
        perf?.reset();
        if (dprChanged) {
            // A new framebuffer size; the timeline itself is unchanged.
            this.measure();
            this.stateDirty = true;
        }
    }
    frameIntervalEstimate = 16.7;
    submitPass(gl, bundle, state, pointer, regions, warpLayer) {
        const preset = this.preset;
        const palette = this.palette;
        const loc = bundle.loc;
        gl.useProgram(bundle.program);
        if (bundle.position !== this.activePosition) {
            if (this.activePosition >= 0)
                gl.disableVertexAttribArray(this.activePosition);
            this.activePosition = bundle.position;
            gl.enableVertexAttribArray(this.activePosition);
            gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer);
            gl.vertexAttribPointer(this.activePosition, 2, gl.FLOAT, false, 0, 0);
        }
        if (this.configuredProgramVersions.get(bundle.program) !== this.uniformVersion) {
            this.configuredProgramVersions.set(bundle.program, this.uniformVersion);
            const interaction = preset.interaction;
            const layout = interaction.viewport;
            gl.uniform2f(loc.resolution, this.canvas.width, this.canvas.height);
            gl.uniform2f(loc.cssResolution, this.cssWidth, this.cssHeight);
            gl.uniform1f(loc.framingAspect, this.framingAspect);
            gl.uniform1f(loc.sceneHeightRatio, this.sceneHeightRatio);
            gl.uniform3f(loc.palettePhase, palette.phase[0], palette.phase[1], palette.phase[2]);
            gl.uniform1f(loc.paletteStart, palette.sweepStart);
            gl.uniform1f(loc.paletteSpan, palette.sweepSpan);
            gl.uniform1f(loc.mixAmount, palette.mixAmount);
            gl.uniform1f(loc.curveViewportSpan, this.curveProfile.viewportSpan);
            gl.uniform3f(loc.curveXCheckpoints, ...this.curveProfile.xCheckpoints);
            gl.uniform4f(loc.curveBendWindows, ...this.curveProfile.bendWindows);
            gl.uniform1f(loc.curveStartWidthScale, this.curveProfile.startWidthScale);
            gl.uniform1f(loc.shapeType, preset.shape === "triangle" ? 1 : 0);
            if (bundle.interaction) {
                gl.uniform1f(loc.pointerRadiusPx, this.pointerRadiusPx);
                gl.uniform1f(loc.pointerFeather, interaction.feather);
                gl.uniform1f(loc.pointerCoreExclusion, interaction.coreExclusion);
                gl.uniform1f(loc.pointerMaxMagnification, layout.maxMagnification);
                gl.uniform1f(loc.pointerDotMagnificationGain, interaction.dotMagnificationGain);
                gl.uniform1f(loc.pointerSpreadPx, layout.strandSpreadPx);
                gl.uniform1f(loc.pointerVelocityBiasPx, interaction.velocityBiasPx);
                gl.uniform1f(loc.emissionOccupancy, interaction.emissionOccupancy);
                gl.uniform1f(loc.emissionRadiusScale, interaction.emissionRadiusScale);
                gl.uniform1f(loc.emissionSoftnessScale, interaction.emissionSoftnessScale);
                gl.uniform2f(loc.emissionPeelPx, ...interaction.emissionPeelDistancePx);
                gl.uniform2f(loc.emissionLifetime, ...interaction.emissionLifetimeSeconds);
                gl.uniform1f(loc.emissionOpacity, interaction.emissionOpacity);
                gl.uniform1f(loc.emissionSeed, interaction.emissionSeed);
            }
            const halftone = preset.halftone;
            gl.uniform1f(loc.halftoneGranularity, halftone.granularity);
            gl.uniform2f(loc.halftoneRadius, ...halftone.radius);
            gl.uniform2f(loc.halftoneFoldFadeAnisotropy, ...halftone.foldFadeAnisotropy);
            gl.uniform1f(loc.halftoneLineFloor, halftone.lineFloor);
            gl.uniform1f(loc.halftoneDotGain, halftone.dotGain);
            gl.uniform1f(loc.halftoneEdgeSoftness, halftone.edgeSoftness);
            gl.uniform1f(loc.halftoneBloomGain, halftone.bloomGain);
            gl.uniform1f(loc.halftoneSeed, halftone.seed);
        }
        gl.uniform1f(loc.time, this.time);
        if (this.configuredStateVersions.get(bundle.program) !== this.stateVersion) {
            this.configuredStateVersions.set(bundle.program, this.stateVersion);
            gl.uniform1f(loc.crossAxisScale, this.crossAxisScale / Math.max(state.ribbonWidthScale, 0.001));
            gl.uniform3f(loc.bgColor, ...state.background);
            gl.uniform1f(loc.zoom, state.zoom);
            gl.uniform2f(loc.center, ...state.center);
            gl.uniform1f(loc.lineExtent, state.lineExtent);
            gl.uniform1f(loc.rotation, state.rotation);
            gl.uniform1f(loc.spatialColor, state.spatialColor);
            gl.uniform1f(loc.spacing, state.spacing);
            gl.uniform1f(loc.dither, state.dither);
            gl.uniform1f(loc.amplitude, state.turbulence);
            gl.uniform1f(loc.speed, state.speed * IDLE_SPEED_MULTIPLIER);
            gl.uniform1f(loc.twistRetention, state.twistRetention);
            gl.uniform1f(loc.fanSpread, state.fanSpread);
            gl.uniform1f(loc.twistSpread, state.twistSpread);
            gl.uniform1f(loc.horizontalStretch, state.horizontalStretch);
            gl.uniform1f(loc.unravel, state.unravel);
            gl.uniform1f(loc.funnelStrength, state.funnelStrength);
            gl.uniform1f(loc.funnelWidth, state.funnelWidth);
            gl.uniform1f(loc.funnelTaperCeiling, state.funnelTaperCeiling);
            gl.uniform1f(loc.funnelContourFloor, state.funnelContourFloor);
            gl.uniform1f(loc.funnelTurbulence, state.funnelTurbulence);
            gl.uniform1f(loc.funnelSpacing, state.funnelSpacing);
            gl.uniform1f(loc.funnelLineDefinition, state.funnelLineDefinition);
            gl.uniform1f(loc.curveStrength, state.curveStrength);
            gl.uniform1f(loc.curveDisplacementScale, state.curveDisplacementScale);
            gl.uniform1f(loc.curveProgress, state.curveProgress);
            gl.uniform1f(loc.effectOpacity, state.effectOpacity);
            gl.uniform1f(loc.edgeReveal, state.edgeReveal);
            gl.uniform1f(loc.coverageMargin, this.coverageEnabled && !bundle.interaction ? coverageMarginForState(state) : 0);
            gl.uniform1f(loc.pointerStrengthScale, state.pointerStrengthScale);
            gl.uniform1f(loc.pointerFunnelStrengthScale, state.pointerFunnelStrengthScale);
            gl.uniform1f(loc.halftoneApparentZoom, state.halftoneApparentZoom);
        }
        if (bundle.interaction) {
            gl.uniform2f(loc.pointerPx, pointer.x, pointer.y);
            gl.uniform2f(loc.pointerVelocity, pointer.velocityX, pointer.velocityY);
            gl.uniform1f(loc.pointerStrength, pointer.strength * preset.interaction.strength);
        }
        if (warpLayer !== undefined && this.warpField) {
            gl.uniform1i(loc.warpLayer, warpLayer);
            gl.uniform2f(loc.warpSize, this.warpField.width, this.warpField.height);
        }
        else if (this.warpEnabled) {
            gl.uniform1i(loc.warpField, 0);
        }
        if (regions) {
            for (const rect of regions) {
                if (rect[2] <= 0 || rect[3] <= 0)
                    continue;
                gl.scissor(...rect);
                gl.drawArrays(gl.TRIANGLES, 0, 6);
            }
        }
        else
            gl.drawArrays(gl.TRIANGLES, 0, 6);
    }
    submit(gl, bundle, state, pointer, timestamp, cpuStart, capture = false) {
        if (this.warpEnabled && (!this.warpField || !this.warpBundle))
            return;
        const perf = capture ? null : this.perf;
        const query = perf?.beginGpuQuery() ?? null;
        const idle = bundle.interaction && this.optimized && this.diagnostics?.get("auroraPointerRegions") !== "0"
            ? this.bundleForState(state, false) : null;
        const split = idle && !idle.interaction;
        try {
            if (this.warpEnabled && this.warpField && this.warpBundle) {
                const field = this.warpField;
                gl.activeTexture(gl.TEXTURE0);
                gl.bindTexture(gl.TEXTURE_2D_ARRAY, null);
                gl.bindFramebuffer(gl.FRAMEBUFFER, field.framebuffer);
                gl.viewport(0, 0, field.width, field.height);
                for (let layer = 0; layer < 20; layer += 4) {
                    for (let attachment = 0; attachment < 4; attachment++) {
                        gl.framebufferTextureLayer(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0 + attachment, field.texture, 0, layer + attachment);
                    }
                    this.submitPass(gl, this.warpBundle, state, pointer, undefined, layer);
                }
                gl.bindFramebuffer(gl.FRAMEBUFFER, null);
                gl.viewport(0, 0, this.canvas.width, this.canvas.height);
                gl.bindTexture(gl.TEXTURE_2D_ARRAY, field.texture);
            }
            if (split) {
                const regions = pointerDrawRegions(this.canvas.width, this.canvas.height, this.cssWidth, this.cssHeight, pointer.x, pointer.y, this.pointerRadiusPx);
                gl.enable(gl.SCISSOR_TEST);
                this.submitPass(gl, idle, state, pointer, regions.idle);
                this.submitPass(gl, bundle, state, pointer, [regions.interactive]);
            }
            else
                this.submitPass(gl, bundle, state, pointer);
        }
        finally {
            gl.bindFramebuffer(gl.FRAMEBUFFER, null);
            gl.viewport(0, 0, this.canvas.width, this.canvas.height);
            gl.disable(gl.SCISSOR_TEST);
            perf?.endGpuQuery(query);
        }
        if (capture)
            return;
        this.lastSubmittedTime = this.time;
        const margin = this.coverageEnabled ? coverageMarginForState(state) : 0;
        perf?.setRenderState("rendering");
        perf?.setFrameInfo(bundle.variant, [this.cssWidth, this.cssHeight], [this.canvas.width, this.canvas.height], split ? "pointer region + idle" : margin !== 0 && !bundle.interaction ? `bounded:${margin < 0 ? "deformation" : margin.toFixed(2)}` : "full");
        perf?.recordFrame(timestamp, performance.now() - cpuStart);
    }
    /**
     * Diagnostics only: draw the current state now and read the pixels back in
     * the same task (the drawing buffer is not preserved across tasks).
     */
    capture(options = {}) {
        const gl = this.gl;
        if (!gl || this.contextLost || !this.preset || !this.initialState ||
            (this.warpEnabled && (!this.warpField || !this.warpBundle)))
            return null;
        let state = this.lastRenderedState ?? this.currentState;
        if (!state)
            return null;
        if (options.revealProgress !== undefined && this.timedReveal) {
            const progress = clamp(options.revealProgress, 0, 1);
            state = { ...this.timedReveal.state, effectOpacity: easeOutCubic(progress), edgeReveal: progress };
        }
        const pointer = options.pointer ?? this.lastPointer;
        const bundle = this.bundleForState(state, pointer.strength > 0.0001);
        if (!bundle)
            return null;
        const previousTime = this.time;
        try {
            this.time = options.time ?? this.time;
            this.stateVersion += 1;
            this.submit(gl, bundle, state, pointer, performance.now(), 0, true);
            const pixels = new Uint8Array(this.canvas.width * this.canvas.height * 4);
            gl.readPixels(0, 0, this.canvas.width, this.canvas.height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
            return { width: this.canvas.width, height: this.canvas.height, pixels };
        }
        finally {
            this.time = previousTime;
            this.stateVersion += 1;
            this.stateDirty = true;
            this.lastClear = null;
        }
    }
}
//# sourceMappingURL=engine.js.map